import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchGISServices } from '@rsos/reduxDataStore/src/features/gisServices/gisServicesSlice';
import checkGISServicesAccess from '@rsos/utils/checkGISServicesAccess';

const TWENTY_FOUR_HOURS = 86400;

/**
 * A GIS token hook if the agency has GIS services capability enabled
 */
const useGISToken = () => {
  const dispatch = useDispatch();

  const orgName = useSelector(state => state.sinatra.user.currentOrg.name);

  const hasGISServicesAccess = useSelector(state =>
    checkGISServicesAccess({
      psaps: state.psaps,
      sinatra: state.sinatra,
    }),
  );

  const gisIdentities = useSelector(state => state.gisServices.gisIdentities);

  // GIS token expiration value is the same throughout the GIS identities array
  const tokenExpiresIn =
    gisIdentities?.[0]?.tokenExpiresIn || TWENTY_FOUR_HOURS;

  // Fetch GIS services on load
  useEffect(() => {
    if (hasGISServicesAccess) {
      dispatch(fetchGISServices(orgName));
    }
  }, [dispatch, hasGISServicesAccess, orgName]);

  // Renew GIS token based on token expiration time
  useEffect(() => {
    const expirationThreshold = 1000 * 60 * 5;
    const expirationTime = tokenExpiresIn * 1000 - expirationThreshold || 0;
    const currentTime = new Date().getTime();
    const shouldRenew = expirationTime > 0 && expirationTime <= currentTime;

    if (hasGISServicesAccess && shouldRenew) {
      const renewGISToken = setTimeout(() => {
        dispatch(fetchGISServices(orgName));
      }, expirationTime);

      return () => clearTimeout(renewGISToken);
    }
  }, [dispatch, hasGISServicesAccess, orgName, tokenExpiresIn]);
};

export default useGISToken;
